import { SpanStatusCode, trace } from '@opentelemetry/api'

import { APP_NAME } from '../config/costants'

/**
 * Promise rejections can carry any value; wrap non-Error reasons so they have a message and a stack to record.
 */
export const toError = (reason: unknown): Error => (reason instanceof Error ? reason : new Error(String(reason)))

/**
 * This is a TypeScript function that traps and records Windows exceptions in a span for tracing
 * purposes.
 * @param {Error | undefined} error - The error object that was caught and needs to be processed.
 * @param {string | Event} [_message] - The optional message parameter can be a string or an Event
 * object that provides additional information about the error.
 * @param {string | undefined} [_source] - The _source parameter is a string that represents the source
 * of the error, such as the name of the file or module where the error occurred. It is an optional
 * parameter and may be undefined.
 * @param {number | undefined} [_lineno] - The _lineno parameter is an optional parameter that
 * represents the line number where the error occurred.
 * @param {number | undefined} [_colno] - _colno is an optional parameter that represents the column
 * number where the error occurred in the source code. It is typically used in conjunction with the
 * _lineno parameter to provide more specific information about the location of the error.
 * @returns a boolean value, either `true` or `undefined`. If there is an error and it is not being
 * processed by `componentDidCatch`, the function will create a span and record the exception before
 * ending the span and returning `true`. If there is no error or the error is being processed by
 * `componentDidCatch`, the function will not create a span and will return `undefined
 *
 */
export const windowsErrorTrap = (
  error: Error | undefined,
  _message?: string | Event,
  _source?: string | undefined,
  _lineno?: number | undefined,
  _colno?: number | undefined,
) => {
  if (error) {
    // XXX Ignore errors that will be processed by componentDidCatch.
    // SEE: https://github.com/facebook/react/issues/10474
    if (error.stack && error.stack.indexOf('invokeGuardedCallbackDev') == -1) {
      const tracer = trace.getTracer(APP_NAME)
      // Create a span.
      const span = tracer.startSpan('windowsExceptionTrap')
      span.recordException(error)
      span.setStatus({ code: SpanStatusCode.ERROR })
      span.end()
      return true
    }
  }
}
